import { Trans, useTranslation } from 'react-i18next'
import { useApp } from '../hooks/useApp'
import { callAxios } from '../hooks/useAxios'
import { Device, DeviceHwID, ID, MultioptConfigMode } from '../types/models'
import { TopTitle } from '../components/atoms/TopTitle'
import { useNavigate, useParams } from 'react-router-dom'
import { useDevices } from '../hooks/useDevices'
import { useEffect, useState } from 'react'
import { SelectRadioForm } from '../components/forms/SelectRadioForm'
import { Button } from '../components/atoms/Button'
import {
  Box,
  Stack,
  Dialog,
  DialogTitle,
  Typography,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
} from '@mui/material'
import { useAuth } from '../hooks/useAuth'
import { RadioPortControl } from '../classes/ports/RadioPortControl'
import { DeviceControl } from '../classes/DeviceControl'
import { delay } from '../utils/time'
import { Appliance } from '../classes/appliances/Appliance'

interface MultiOptimizationRoleSelectionProps {
  value: MultioptConfigMode
  onChange: (value: MultioptConfigMode) => void
}

/**
 * Radio buttons for the device's role in multi-device optimization.
 *
 * Kept at module level on purpose: when this was a function declared inside
 * RadioConfigPage, every render of the page produced a new component type, so
 * React remounted the whole RadioGroup (new <input> elements) on each websocket
 * update. A click that started on a radio or its label landed on an element
 * that no longer existed by mouseup, and the browser fired no click at all —
 * the roles could not be selected on a live location.
 */
function MultiOptimizationRoleSelection({ value, onChange }: MultiOptimizationRoleSelectionProps) {
  const { t } = useTranslation()

  return (
    <>
      <FormLabel><Trans>Device role on multi-device optimization:</Trans></FormLabel>
      <RadioGroup
        value={value}
        onChange={(event) => { onChange(event.target.value as MultioptConfigMode) }}
      >
        <FormControlLabel
          value='MULTIOPT_CONFIG_AUTO'
          control={<Radio />}
          label={t('Automatic')}
        />
        <FormControlLabel value='MULTIOPT_CONFIG_LEADER' control={<Radio />} label={t('Leader')} />
        <FormControlLabel
          value='MULTIOPT_CONFIG_FOLLOWER'
          control={<Radio />}
          label={t('Follower')}
        />
        <FormControlLabel
          value='MULTIOPT_CONFIG_NOOPT'
          control={<Radio />}
          label={t('No multi-device optimization')}
        />
      </RadioGroup>
    </>
  )
}

export function RadioConfigPage() {
  const app = useApp()
  const { control } = useDevices()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [devices, setDevices] = useState<Device[]>([])
  const [deletable, setDeletable] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const { updateMe } = useAuth()

  const { id } = useParams()
  const deviceControl: DeviceControl = control.controls({ deviceId: parseInt(id || '0') as ID })[0]
  const device = deviceControl ? deviceControl.device : null

  const [master, setMaster] = useState<DeviceHwID | ''>(device?.values?.slave_of || '')

  const isMaster = device?.values?.master_of && device?.values?.master_of.length > 0

  // Unset mode means "Automatic": that is how SystemControl.multiOptRoles() and
  // the server (Sunergos.Manager.MultiOpt) already treat it. "No multi-device
  // optimization" is stored only when the user explicitly selects it.
  const [multiOptSelection, setMultiOptSelection] = useState<MultioptConfigMode>(
    device?.values?.multiopt_config_mode ?? 'MULTIOPT_CONFIG_AUTO'
  )

  useEffect(() => {
    callAxios<Device[]>({
      url: '/devices',
    }).then(res => res.success && setDevices(res.data.filter(d => d.hw_id !== device?.hw_id)))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!device) {
    return <></>
  }

  const onSave = async (): Promise<void> => {
    if (devices.length === 0) {
      navigate('/devices')
    } else {
      setSaving(true)

      const deviceRadio: RadioPortControl = deviceControl.findPortByType('RADIO') as RadioPortControl
      const masterRadio: RadioPortControl | null = master ? control.ports({ deviceHwId: master as DeviceHwID, portType: 'RADIO' })[0] as RadioPortControl : null
      const oldMasterRadio: RadioPortControl | null = device?.values?.slave_of ? control.ports({ deviceHwId: device?.values?.slave_of as DeviceHwID, portType: 'RADIO' })[0] as RadioPortControl : null

      if (!device?.values?.slave_of && masterRadio) {
        // There was no master, but now we have.
        await masterRadio.addSlave(device.hw_id as DeviceHwID)
        await delay(1000)
        await deviceRadio.joinMaster(master as DeviceHwID)
      } else if (device?.values?.slave_of && !masterRadio && oldMasterRadio) {
        // There was master, but not anymore.
        await oldMasterRadio.removeSlave(device.hw_id)
        await delay(1000)
        await deviceRadio.leaveMaster()
      } else if (oldMasterRadio && masterRadio && device?.values?.slave_of !== master) {
        // Changing old master to new master.
        await oldMasterRadio.removeSlave(device.hw_id)
        await delay(1000)
        await masterRadio.addSlave(device.hw_id as DeviceHwID)
        await delay(1000)
        await deviceRadio.joinMaster(master as DeviceHwID)
      }

      // Update device multi-optimization configuration mode
      await deviceControl.saveDeviceValues({ multiopt_config_mode: multiOptSelection })

      // Update other devices based on the selected multi-optimization mode
      if (multiOptSelection === 'MULTIOPT_CONFIG_AUTO') {
        // Switch all other devices to automatic mode (except those with no-opt value)
        await Promise.all(
          devices.filter(d => d.values?.multiopt_config_mode !== 'MULTIOPT_CONFIG_NOOPT')
            .map(d => control.getDeviceControl(d.id as ID)
              .saveDeviceValues({ multiopt_config_mode: 'MULTIOPT_CONFIG_AUTO' })
            )
        )
      } else if (multiOptSelection === 'MULTIOPT_CONFIG_LEADER') {
        // Switch all other devices to follower mode (except those with no-opt value)
        await Promise.all(
          devices.filter(d => d.values?.multiopt_config_mode !== 'MULTIOPT_CONFIG_NOOPT')
            .map(d => control.getDeviceControl(d.id as ID)
              .saveDeviceValues({ multiopt_config_mode: 'MULTIOPT_CONFIG_FOLLOWER' })
            )
        )
      }

      // Reset multi-optimization roles and flash devices
      updateMe().then(async () => {
        const items = await Appliance.configureMultiOpt(control)
        await control.flashItems(items)
        control.clearPendingConfig()

        setSaved(true)
        setSaving(false)
      })
    }
  }

  const onContinue = () => {
    navigate('/devices')
  }

  const onDelete = async (): Promise<void> => {
    if (device?.values?.slave_of) {
      const oldMasterRadio: RadioPortControl = control.ports({ deviceHwId: device?.values?.slave_of as DeviceHwID, portType: 'RADIO' })[0] as RadioPortControl

      if (!(await oldMasterRadio.removeSlave(device.hw_id))) {
        app.addError(t('Removing slave failed.'))
        return
      }
    }

    await callAxios({
      method: 'DELETE',
      url: `/devices/claim/${device && device.id}`
    }).then((res) => {
      if (res.success) {
        updateMe().then(async () => {
          const items = await Appliance.configureMultiOpt(control)
          await control.flashItems(items)
          control.clearPendingConfig()

          navigate('/devices')
        })
      } else {
        app.addError(res)
      }
    })
  }

  return (
    <>
      <TopTitle title={t('Configure Device {hwid}').replace('{hwid}', device ? device.hw_id : '')}></TopTitle>

      <Typography data-cy="lan-ip" sx={{ mb: 2 }}>
        {t('LAN IP address')}: {device?.values?.lan_ip || t('not known')}
      </Typography>

      <FormControl>
          {
            !saved &&
              <SelectRadioForm disabled={isMaster} master={master} masters={devices.filter(d => !d.values?.slave_of)} onChange={s => setMaster(s)}/>
          }

          {!saved && devices.length > 0 &&
            <MultiOptimizationRoleSelection value={multiOptSelection} onChange={setMultiOptSelection}/>}
      </FormControl>

      <Button fullWidth disabled={saving} data-cy={`button-${saved ? 'Continue' : 'Next'}`} onClick={saved ? onContinue : onSave}>
          {devices.length > 0 ? (saved ? app.t('Continue') : app.t('Save')) : app.t('Back')}
      </Button>

      <Button fullWidth sx={{ mt: 4, backgroundColor: 'error.dark' }} data-cy="button-Delete" disabled={isMaster || saving} onClick={() => setDeletable(true)}>
          <Trans>Delete</Trans>
      </Button>

      <Dialog open={!!deletable} onClose={() => setDeletable(false)}>
        <DialogTitle sx={{ backgroundColor: 'error.light' }}><Trans>Delete Device?</Trans></DialogTitle>
        <Box sx={{ p: 2 }}>
          <Typography variant="h2">{device && device.hw_id}</Typography>
          <Trans>Are you sure that you want to delete device?</Trans>
          <Stack direction="row" spacing={2} sx={{ m: 2 }}>
            <Button color="primary" onClick={() => setDeletable(false)}><Trans>Cancel</Trans></Button>
            <Button color="secondary" onClick={onDelete}><Trans>OK</Trans></Button>
          </Stack>
        </Box>
      </Dialog>
    </>
  )
}
